import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Button, ConfigProvider, Tooltip, theme as antTheme } from 'antd';
import { DoubleLeftOutlined, DoubleRightOutlined, InfoCircleOutlined } from '@ant-design/icons';

import { sidebarData } from '../../dataSystems/SidebarData';
import ComUtill from '../../utils/comUtil';
import { getSidebarByRole } from './sidebarAccess';

const footerLines = ['시놀(주)', '사업자번호 584-88-03032', '법인등록번호 124411-0358061', '우리은행 1005-304-471988', '고객센터 02-508-5670', '카카오톡 @시놀'];
const navPalette = {
    background: '#1a1a2e',
    border: 'rgba(148, 163, 184, 0.18)',
    collapsedDivider: 'rgba(168, 179, 219, 0.26)',
    panel: 'rgba(255, 255, 255, 0.04)',
    panelHover: 'rgba(255, 255, 255, 0.08)',
    button: 'rgba(255, 255, 255, 0.06)',
    buttonHover: 'rgba(255, 255, 255, 0.12)',
    text: 'rgba(232, 236, 255, 0.9)',
    textMuted: 'rgba(168, 179, 219, 0.7)',
    textSubtle: 'rgba(148, 163, 184, 0.58)',
    active: '#7aa2ff',
    activeBackground: 'rgba(122, 162, 255, 0.18)',
    brandBadge: 'rgba(255, 255, 255, 0.92)',
};
const sidebarTooltipProps = {
    align: {
        offset: [28, 0],
    },
};

const EXPANDED_WIDTH = 320;
const COLLAPSED_WIDTH = 92;

const SideMenubar = ({ collapsed, adminInfo, setCollapsed }) => {
    return (
        <ConfigProvider
            theme={{
                algorithm: antTheme.darkAlgorithm,
                token: {
                    colorBgContainer: navPalette.background,
                    colorBorder: navPalette.border,
                    colorSplit: navPalette.border,
                    colorText: navPalette.text,
                    colorTextDescription: navPalette.textMuted,
                    colorPrimary: navPalette.active,
                    borderRadius: 14,
                },
                components: {
                    Button: {
                        colorText: navPalette.text,
                    },
                },
            }}
        >
            <SideMenubarContent collapsed={collapsed} adminInfo={adminInfo} setCollapsed={setCollapsed} />
        </ConfigProvider>
    );
};

const SideMenubarContent = ({ collapsed, adminInfo, setCollapsed }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { token } = antTheme.useToken();

    const [mode, setMode] = useState('');

    const sideBar = getSidebarByRole(sidebarData, adminInfo);
    const selectedPath = getSelectedPath(location.pathname, sideBar);

    useEffect(() => {
        if (!ComUtill.IsProduction()) {
            setMode('개발모드');
            return;
        }

        setMode('');
    }, []);

    return (
        <SideWrapper $collapsed={collapsed} style={{ backgroundColor: navPalette.background, borderRightColor: navPalette.border }}>
            <SidebarHeader $collapsed={collapsed} style={{ backgroundColor: navPalette.background, borderBottomColor: collapsed ? navPalette.border : 'transparent' }}>
                <BrandButton type="button" $collapsed={collapsed} style={{ background: navPalette.panel }} onClick={() => navigate('/home')}>
                    <BrandIconWrap>
                        <BrandIcon src={`${process.env.PUBLIC_URL}/favicon.ico`} alt="관리자센터" />
                    </BrandIconWrap>
                    {!collapsed && (
                        <BrandTextGroup>
                            <BrandTitle>관리자센터{mode ? ` (${mode})` : null}</BrandTitle>
                            <BrandSubtitle>Sinor Admin Center</BrandSubtitle>
                        </BrandTextGroup>
                    )}
                </BrandButton>
            </SidebarHeader>

            <SidebarScroll $collapsed={collapsed}>
                <SectionList $collapsed={collapsed}>
                    {sideBar.map((section, index) => (
                        <Section key={`${section.mainTitle}-${index}`} $collapsed={collapsed} style={{ borderTopColor: collapsed && index > 0 ? navPalette.collapsedDivider : 'transparent' }}>
                            {!collapsed && <SectionTitle style={{ color: token.colorTextDescription }}>{section.mainTitle}</SectionTitle>}
                            <NavList>
                                {section.subTitleList.map((sub) => {
                                    const active = sub.router_url === selectedPath;
                                    const item = (
                                        <NavButton
                                            key={sub.router_url}
                                            type="button"
                                            $active={active}
                                            $collapsed={collapsed}
                                            style={{
                                                color: active ? navPalette.active : navPalette.text,
                                                backgroundColor: active ? navPalette.activeBackground : 'transparent',
                                            }}
                                            onClick={() => navigate(sub.router_url)}
                                        >
                                            <NavIcon $collapsed={collapsed}>{renderNavIcon(sub.icon)}</NavIcon>
                                            {!collapsed && <NavLabel>{sub.name}</NavLabel>}
                                        </NavButton>
                                    );

                                    if (collapsed) {
                                        return (
                                            <Tooltip key={sub.router_url} placement="right" trigger={['hover', 'focus', 'click']} title={sub.name} {...sidebarTooltipProps}>
                                                {item}
                                            </Tooltip>
                                        );
                                    }

                                    return item;
                                })}
                            </NavList>
                        </Section>
                    ))}
                </SectionList>
                <FooterSection style={{ borderTopColor: collapsed ? navPalette.collapsedDivider : navPalette.border }}>
                    <Footer collapsed={collapsed} />
                </FooterSection>
            </SidebarScroll>

            <BottomDock $collapsed={collapsed} style={{ backgroundColor: navPalette.background, borderTopColor: navPalette.border }}>
                <ToggleArea>
                    <Button block type="text" icon={collapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />} onClick={() => setCollapsed((prev) => !prev)} />
                </ToggleArea>
            </BottomDock>
        </SideWrapper>
    );
};

const getSelectedPath = (pathname, sections) => {
    let bestMatch = '';

    sections.forEach((section) => {
        section.subTitleList.forEach((sub) => {
            const basePath = sub.router_url.replace(/\/:[^/]+/g, '');
            const matches = pathname === basePath || pathname.startsWith(`${basePath}/`);

            if (matches && basePath.length > bestMatch.length) {
                bestMatch = sub.router_url;
            }
        });
    });

    return bestMatch;
};

const renderNavIcon = (icon) => {
    if (!React.isValidElement(icon)) {
        return icon;
    }

    const iconType = icon.type;
    const iconName =
        (typeof iconType === 'function' && (iconType.displayName || iconType.name)) ||
        (typeof iconType === 'object' && (iconType.displayName || iconType.name || iconType.render?.displayName || iconType.render?.name)) ||
        '';
    const isTwoToneIcon = typeof iconType !== 'string' && /TwoTone/.test(iconName);

    return React.cloneElement(icon, {
        ...icon.props,
        ...(isTwoToneIcon ? { twoToneColor: 'currentColor' } : {}),
        style: {
            ...icon.props.style,
            color: 'currentColor',
        },
    });
};

const Footer = ({ collapsed }) => {
    if (collapsed) {
        return (
            <CompactFooter>
                <Tooltip placement="right" trigger={['hover', 'focus', 'click']} title={<FooterTooltipContent />} {...sidebarTooltipProps}>
                    <FooterInfoButton type="button">
                        <InfoCircleOutlined />
                    </FooterInfoButton>
                </Tooltip>
            </CompactFooter>
        );
    }

    return (
        <FooterText>
            {footerLines.map((line) => (
                <div key={line}>{line}</div>
            ))}
        </FooterText>
    );
};

const FooterTooltipContent = () => {
    return (
        <FooterTooltipBox>
            {footerLines.map((line) => (
                <div key={line}>{line}</div>
            ))}
        </FooterTooltipBox>
    );
};

export default SideMenubar;

const SideWrapper = styled.aside`
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    width: ${(props) => (props.$collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`)};
    min-width: ${(props) => (props.$collapsed ? `${COLLAPSED_WIDTH}px` : `${EXPANDED_WIDTH}px`)};
    height: 100%;
    border-right: 1px solid;
    transition:
        width 0.2s ease,
        min-width 0.2s ease;
    overflow: hidden;
`;

const SidebarHeader = styled.div`
    position: sticky;
    top: 0;
    z-index: 2;
    padding: ${(props) => (props.$collapsed ? '8px 14px' : '12px 14px')};
    border-bottom: 1px solid;
`;

const SidebarScroll = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: auto;
    padding: ${(props) => (props.$collapsed ? '0 14px 12px' : '0 14px 14px')};
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
    }
`;

const BrandButton = styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    gap: ${(props) => (props.$collapsed ? '0' : '14px')};
    justify-content: ${(props) => (props.$collapsed ? 'center' : 'flex-start')};
    border: 0;
    border-radius: 18px;
    margin-bottom: ${(props) => (props.$collapsed ? '8px' : '12px')};
    padding: ${(props) => (props.$collapsed ? '10px 0' : '12px 16px')};
    color: inherit;
    cursor: pointer;
`;

const BrandIconWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    min-width: 42px;
    height: 42px;
    border-radius: 14px;
    background: ${navPalette.brandBadge};
`;

const BrandIcon = styled.img`
    width: 24px;
    height: 24px;
    display: block;
`;

const BrandTextGroup = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
`;

const BrandTitle = styled.div`
    color: ${navPalette.text};
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
`;

const BrandSubtitle = styled.div`
    color: ${navPalette.textSubtle};
    font-size: 12px;
    line-height: 1.2;
`;

const SectionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${(props) => (props.$collapsed ? '8px' : '16px')};
`;

const FooterSection = styled.div`
    margin-top: auto;
    padding-top: 12px;
    border-top: 1px solid;
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: ${(props) => (props.$collapsed ? '6px' : '8px')};
    padding-top: ${(props) => (props.$collapsed ? '8px' : '0')};
    border-top: ${(props) => (props.$collapsed ? '1px solid' : '0')};
`;

const SectionTitle = styled.div`
    padding: 0 12px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
`;

const NavList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const NavButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: ${(props) => (props.$collapsed ? 'center' : 'flex-start')};
    width: 100%;
    min-height: 48px;
    gap: ${(props) => (props.$collapsed ? '0' : '12px')};
    border: 0;
    border-radius: 14px;
    padding: ${(props) => (props.$collapsed ? '12px 0' : '12px 14px')};
    text-align: left;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition:
        background-color 0.2s ease,
        color 0.2s ease,
        transform 0.2s ease;

    &:hover {
        background-color: ${navPalette.panelHover} !important;
    }

    &:focus-visible {
        outline: 2px solid ${navPalette.active};
        outline-offset: 2px;
    }
`;

const NavIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: ${(props) => (props.$collapsed ? '24px' : '20px')};
    font-size: 18px;
`;

const NavLabel = styled.span`
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const BottomDock = styled.div`
    position: sticky;
    bottom: 0;
    padding: ${(props) => (props.$collapsed ? '8px 14px' : '12px 14px')};
    border-top: 1px solid;
    backdrop-filter: blur(12px);
`;

const ToggleArea = styled.div`
    margin-bottom: 0;

    .ant-btn {
        height: 44px;
        border-radius: 14px;
        justify-content: center;
        background: ${navPalette.button};
    }

    .ant-btn:hover,
    .ant-btn:focus {
        background: ${navPalette.buttonHover} !important;
    }
`;

const FooterText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px 8px 0;
    color: ${navPalette.textSubtle};
    font-size: 12px;
    line-height: 1.5;
    word-break: keep-all;
`;

const CompactFooter = styled.div`
    display: flex;
    justify-content: center;
    padding-top: 4px;
`;

const FooterInfoButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 0;
    border-radius: 12px;
    background: ${navPalette.button};
    color: ${navPalette.textMuted};
    font-size: 18px;
    cursor: pointer;
`;

const FooterTooltipBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    line-height: 1.5;
`;
